import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus,
  MoreHorizontal,
  Trash2,
  Archive,
  Loader2,
  FileCode,
  Circle,
} from 'lucide-react';
import {
  Notebook,
  listNotebooks,
  createNotebook,
  deleteNotebook,
  updateNotebook,
} from '@/services/notebooksApi';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

export default function NotebooksListPage() {
  const navigate = useNavigate();
  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create dialog state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Load notebooks
  const loadNotebooks = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await listNotebooks(false, 100, 0);
      setNotebooks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notebooks');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotebooks();
  }, [loadNotebooks]);

  // Create notebook
  const handleCreate = async () => {
    if (!createName.trim()) return;

    setIsCreating(true);
    try {
      const notebook = await createNotebook({
        name: createName.trim(),
        description: createDescription.trim() || undefined,
      });
      setIsCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      navigate(`/notebooks/${notebook.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create notebook');
    } finally {
      setIsCreating(false);
    }
  };

  // Delete notebook
  const handleDelete = async (notebookId: string) => {
    if (!confirm('Are you sure you want to delete this notebook?')) return;

    try {
      await deleteNotebook(notebookId);
      setNotebooks((prev) => prev.filter((n) => n.id !== notebookId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete notebook');
    }
  };

  // Archive notebook
  const handleArchive = async (notebookId: string) => {
    try {
      await updateNotebook(notebookId, { is_archived: true });
      setNotebooks((prev) => prev.filter((n) => n.id !== notebookId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive notebook');
    }
  };

  return (
    <div className="container mx-auto py-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Notebooks</h1>
          <p className="text-muted-foreground">
            Interactive Python notebooks for data analysis and agent development
          </p>
        </div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Notebook
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Notebook</DialogTitle>
              <DialogDescription>
                Create a new Python notebook for data analysis and exploration.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="My Notebook"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  placeholder="Describe what this notebook is for..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsCreateOpen(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!createName.trim() || isCreating}
              >
                {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-md">
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && notebooks.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <FileCode className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No notebooks yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first notebook to get started with Python data analysis.
          </p>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Notebook
          </Button>
        </div>
      )}

      {/* Notebook grid */}
      {!isLoading && notebooks.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {notebooks.map((notebook) => (
            <Card
              key={notebook.id}
              className={cn(
                'cursor-pointer hover:border-primary/50 transition-colors',
                notebook.has_active_session && 'border-amber-500/50'
              )}
              onClick={() => navigate(`/notebooks/${notebook.id}`)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">
                      {notebook.name}
                    </CardTitle>
                    <CardDescription className="mt-1 line-clamp-2">
                      {notebook.description || 'No description'}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchive(notebook.id);
                        }}
                      >
                        <Archive className="w-4 h-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notebook.id);
                        }}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>{notebook.cell_count} cells</span>
                  <span>
                    Updated{' '}
                    {formatDistanceToNow(new Date(notebook.updated_at), {
                      addSuffix: true,
                    })}
                  </span>
                  {notebook.has_active_session && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <Circle className="w-2 h-2 fill-current" />
                      Session
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
